import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog'; // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
import { GrowlerService } from '../../../../shared/growler/growler.service';
import { PayablesService } from '../../../../../services/payables/payables.service';
import { finalize } from 'rxjs/operators';
import moment from 'moment-timezone';
import { LegFiJwtService } from '../../../../../services/auth/legfi-jwt.service';
import { CommonModule } from '@angular/common';
import { LegfiDatePipe } from 'app/pipes/format/legfi-date.pipe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const TRACKING_STATUSES = {
    CREATED: 'Created',
    MAILED: 'Mailed',
    IN_TRANSIT: 'In Transit',
    IN_LOCAL_AREA: 'In Local Area',
    PROCESSED_FOR_DELIVERY: 'Processed for Delivery',
    DELIVERED: 'Delivered'
};

const TRACKING_SEQUENCE = [
    TRACKING_STATUSES.CREATED,
    TRACKING_STATUSES.MAILED,
    TRACKING_STATUSES.IN_TRANSIT,
    TRACKING_STATUSES.IN_LOCAL_AREA,
    TRACKING_STATUSES.PROCESSED_FOR_DELIVERY,
    TRACKING_STATUSES.DELIVERED
];

export interface StatusUpdate {
    name: string;
    location?: string;
    eventTime?: moment.Moment;

    // UI fields
    completed: boolean;
    inProgress?: boolean;
    estimatedDate?: moment.Moment; // figma plan
}

export interface TrackMailDialogInput {
    title: string;
    payableId: number;
}

@UntilDestroy()
@Component({
    selector: 'app-track-mail-dialog',
    templateUrl: './track-mail-dialog.component.html',
    styleUrls: ['./track-mail-dialog.component.scss'],
    standalone: true,
    imports: [CommonModule,
        LegfiDatePipe,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TrackMailDialogComponent implements OnInit {
    isLoading = false;
    trackingNumber: string = null;
    payableData: any = null;
    statusUpdates: StatusUpdate[] = [];

    constructor(
            private _dialogRef: MatDialogRef<TrackMailDialogComponent>, // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
            private _growlerService: GrowlerService,
            private _payablesService: PayablesService,
            @Inject(MAT_DIALOG_DATA) public data: TrackMailDialogInput, // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
    ) {
        this._dialogRef.addPanelClass('kit-dialog');
    }

    get dialogTitle(): string {
        return this.data.title;
    }

    ngOnInit(): void {
        this.isLoading = true;
        this._payablesService.getPayableTrackingData(this.data.payableId)
                .pipe(finalize(() => this.isLoading = false), untilDestroyed(this))
                .subscribe({
                    next: (response) => {
                        this.payableData = response;
                        this.trackingNumber = response.billPayment?.trackingNumber;

                        if (response.events && response.events.length) {
                            this.statusUpdates = response.events.map(event => ({
                                name: event.name,
                                location: event.location,
                                eventTime: moment(event.eventTime),
                                completed: true,
                                inProgress: false
                            }));

                            // Mark latest event as in progress
                            if (this.statusUpdates.length > 0) {
                                this.statusUpdates[this.statusUpdates.length - 1].inProgress = true;
                            }
                        } else {
                            // No events yet, add a Created step based on bill payment data
                            this.statusUpdates.push({
                                name: TRACKING_STATUSES.CREATED,
                                eventTime: response.billPayment.sendDate ?
                                        moment.tz(response.billPayment.sendDate, LegFiJwtService.getTimezone()) :
                                        response.billPayment.scheduledPaymentDate ?
                                                moment.tz(response.billPayment.scheduledPaymentDate.substring(0, 10), 'YYYY-MM-DD', LegFiJwtService.getTimezone()) :
                                                moment(),
                                completed: true,
                                inProgress: true
                            });

                            // Add expected future steps without estimated dates for now
                            for (let i = 1; i < TRACKING_SEQUENCE.length; i++) {
                                const stepName = TRACKING_SEQUENCE[i];
                                this.statusUpdates.push({
                                    name: stepName,
                                    completed: false,
                                    inProgress: false
                                });
                            }
                        }
                    },
                    error: () => {
                        this._growlerService.oops('Failed to load tracking information');
                    }
                });
    }

    close(): void {
        this._dialogRef.close();
    }

    trackByStatusName(index: number, update: StatusUpdate): string {
        return update.name;
    }
}
